/**
 * The page a `twasta new` / `twasta mount` sign-in link opens.
 *
 * WHY IT IS ITS OWN PAGE. The CLI used to print the Settings deep link, which
 * dropped people on a screen full of token administration and asked them to
 * find the right box and retype a code they had just read off their terminal.
 * This page does exactly one thing, and when the link carries `?code=` it does
 * it without any typing: look up the request, show WHO is asking, Allow / Deny.
 *
 * The code in the URL is not a credential — it identifies a pending request,
 * and this page is behind ProtectedRoute, so the identity that gets minted is
 * the signed-in browser session's, never anything the link carried. A stranger
 * who guesses a code still has to be logged in as someone.
 */
import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Laptop, ShieldCheck, Terminal, XCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import api from '@/api/client'

interface Pending {
  user_code: string
  project: string | null
  surface: string
  client_info: string | null
  expires_at: string | null
}

const SURFACE_LABEL: Record<string, string> = {
  ssh: 'a terminal on the Twasta server (SSH)',
  remote: 'a coding agent on your own computer',
  any: 'a coding agent',
}

export default function AgentApprovePage() {
  const [params, setParams] = useSearchParams()
  const [code, setCode] = useState((params.get('code') || '').toUpperCase())
  const [pending, setPending] = useState<Pending | null>(null)
  const [checking, setChecking] = useState(false)
  const [done, setDone] = useState<'approved' | 'denied' | null>(null)
  const [error, setError] = useState('')

  const lookup = useCallback(async (raw: string) => {
    const value = raw.trim().toUpperCase()
    if (!value) return
    setChecking(true)
    setError('')
    try {
      const { data } = await api.get('/agent-access/device/pending', {
        params: { user_code: value },
      })
      setPending(data)
    } catch (err: any) {
      setPending(null)
      setError(err?.response?.data?.detail
        || 'That code is unknown, already used, or has expired.')
    } finally {
      setChecking(false)
    }
  }, [])

  // A link that carries the code needs no interaction at all before the
  // question is on screen — that is the entire point of the deep link.
  useEffect(() => {
    const fromUrl = params.get('code')
    if (fromUrl) void lookup(fromUrl)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const approve = async () => {
    if (!pending) return
    try {
      await api.post('/agent-access/device/approve', {
        user_code: pending.user_code,
        name: pending.client_info || 'device login',
      })
      setDone('approved')
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Could not approve')
    }
  }

  const deny = async () => {
    if (!pending) return
    try {
      await api.post('/agent-access/device/deny', { user_code: pending.user_code })
      setDone('denied')
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Could not deny')
    }
  }

  const card: React.CSSProperties = {
    background: 'var(--color-bg-secondary)',
    border: '1px solid var(--color-border)',
    borderRadius: 'var(--radius-lg)',
    padding: '2rem',
    maxWidth: 560,
    margin: '3rem auto',
  }
  const button: React.CSSProperties = {
    padding: '0.625rem 1.25rem', fontSize: '1rem', fontWeight: 500,
    borderRadius: 'var(--radius-md)', cursor: 'pointer', border: 'none',
  }

  if (done) {
    return (
      <div style={card}>
        <div style={{ textAlign: 'center' }}>
          {done === 'approved' ? (
            <ShieldCheck size={40} style={{ color: 'var(--color-success, #22c55e)' }} />
          ) : (
            <XCircle size={40} style={{ color: 'var(--color-error, #ef4444)' }} />
          )}
          <h2 style={{ fontSize: '1.3125rem', fontWeight: 600, margin: '12px 0 8px' }}>
            {done === 'approved' ? 'Access granted' : 'Request denied'}
          </h2>
          <p style={{ color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
            {done === 'approved'
              ? 'You can go back to your terminal now — it will continue on its own within a few seconds.'
              : 'Nothing was granted. The waiting terminal will say the request was denied.'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <Terminal size={20} style={{ color: 'var(--color-accent)' }} />
        <h2 style={{ fontSize: '1.3125rem', fontWeight: 600 }}>
          Grant access to your computer?
        </h2>
      </div>
      <p style={{
        fontSize: '0.9375rem', color: 'var(--color-text-secondary)',
        lineHeight: 1.6, marginBottom: 20,
      }}>
        A coding agent is asking to work on your Twasta projects as you. It can
        only reach projects you already have access to, and you can revoke it at
        any time in Settings → Agent access.
      </p>

      {!pending && (
        <>
          {/* Only shown when the link carried no code, or the code was bad —
              the happy path never asks anyone to type this. */}
          <label style={{
            display: 'block', fontSize: '0.875rem', fontWeight: 500,
            marginBottom: 6,
          }}>
            Enter the code shown in your terminal
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              autoFocus
              value={code}
              placeholder="ABCD-1234"
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => { if (e.key === 'Enter') void lookup(code) }}
              style={{
                flex: 1, padding: '0.625rem 0.75rem', fontSize: '1.0625rem',
                letterSpacing: '0.08em', fontFamily: 'monospace',
                background: 'var(--color-bg-primary)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-md)', color: 'var(--color-text)',
              }}
            />
            <button
              onClick={() => void lookup(code)}
              disabled={checking || !code.trim()}
              style={{
                ...button, background: 'var(--color-accent)', color: '#fff',
                opacity: checking || !code.trim() ? 0.6 : 1,
              }}
            >{checking ? 'Checking…' : 'Continue'}</button>
          </div>
          {error && (
            <p style={{
              marginTop: 12, fontSize: '0.875rem',
              color: 'var(--color-error, #ef4444)',
            }}>{error}</p>
          )}
        </>
      )}

      {pending && (
        <>
          <div style={{
            background: 'var(--color-bg-primary)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)', padding: '1rem', marginBottom: 20,
          }}>
            {[
              ['Code', pending.user_code],
              ['Asking from', SURFACE_LABEL[pending.surface] || pending.surface],
              ['Machine', pending.client_info || 'not reported'],
              ...(pending.project ? [['Project', pending.project]] : []),
            ].map(([label, value]) => (
              <div key={label} style={{
                display: 'flex', gap: 12, fontSize: '0.9375rem',
                padding: '4px 0',
              }}>
                <span style={{
                  width: 110, flexShrink: 0,
                  color: 'var(--color-text-secondary)',
                }}>{label}</span>
                <span style={{ fontWeight: 500, wordBreak: 'break-word' }}>{value}</span>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              onClick={() => void approve()}
              style={{ ...button, background: 'var(--color-accent)', color: '#fff' }}
            >
              <Laptop size={15} style={{ verticalAlign: -2, marginRight: 6 }} />
              Allow access
            </button>
            <button
              onClick={() => void deny()}
              style={{
                ...button, background: 'transparent',
                color: 'var(--color-text-secondary)',
                border: '1px solid var(--color-border)',
              }}
            >Deny</button>
          </div>
          <p style={{
            marginTop: 14, fontSize: '0.8125rem',
            color: 'var(--color-text-secondary)',
          }}>
            Didn't start this? Click Deny — nothing is granted until you allow it.
          </p>
        </>
      )}

      {!pending && params.get('code') && !checking && (
        <button
          onClick={() => { setParams({}); setError(''); setCode('') }}
          style={{
            ...button, marginTop: 14, background: 'transparent',
            color: 'var(--color-text-secondary)',
            border: '1px solid var(--color-border)', fontSize: '0.875rem',
          }}
        >Enter a different code</button>
      )}
    </div>
  )
}
